// TUT-488 — o período de/até com atalhos: a conta que o `FiltroDePeriodo`
// (`components/ui/filtro-de-periodo.tsx`) usa. Nasceu no quadro de Propostas
// porque não havia um componente de período compartilhado nas listas (cada
// tela tinha o seu par de `<input type="date">`), e o construtor de relatórios
// vai reusá-lo. A conta é em DIAS de calendário (`AAAA-MM-DD`): quem sabe o
// fuso é quem diz qual é "hoje" (`calendarDateKey`, de `lib/format`).
//
// TUT-476 (integração) — este é o ÚNICO módulo de período de dias da web. Três
// ajudantes nasceram em paralelo (o do quadro de Propostas, o do recorte de
// laudos da Elaboração e o do link dos widgets) e foram unificados aqui: o dia
// que EXISTE no calendário (`diaDeCalendario`), o par `periodoDe`/`periodoAte`
// saneado da URL (`periodoDaBusca`), o texto do chip (`rotuloDoPeriodo`) e o
// fim do balde da Série (`ultimoDiaDoBalde`). Quem precisa de outro, acrescenta
// aqui: `datas-no-fuso-do-escritorio.test.ts` reprova a cópia.

/** `de`/`ate` em `AAAA-MM-DD`; vazio = aberto daquele lado. */
export interface PeriodoDeDatas {
  de: string;
  ate: string;
}

export type AtalhoDePeriodo = "30-dias" | "90-dias" | "ano";

export const ATALHOS_DE_PERIODO: { chave: AtalhoDePeriodo; rotulo: string }[] = [
  { chave: "30-dias", rotulo: "Últimos 30 dias" },
  { chave: "90-dias", rotulo: "Últimos 90 dias" },
  { chave: "ano", rotulo: "Este ano" },
];

export function somarDias(dia: string, dias: number): string {
  const [ano = 0, mes = 1, d = 1] = dia.split("-").map(Number);
  return new Date(Date.UTC(ano, mes - 1, d + dias)).toISOString().slice(0, 10);
}

const DIA = /^(\d{4})-(\d{2})-(\d{2})$/u;

/** O dia `AAAA-MM-DD` quando ele EXISTE no calendário; senão, nada (31/02 cai). */
export function diaDeCalendario(valor: unknown): string | undefined {
  if (typeof valor !== "string") return undefined;
  const partes = DIA.exec(valor);
  if (!partes) return undefined;
  const [, ano, mes, dia] = partes.map(Number) as [number, number, number, number];
  // Remontado em UTC e conferido campo a campo (31/02 vira 03/03 e não volta
  // igual). Sem `toISOString().slice(0, 10)`: "hoje" é o dia do escritório, e
  // a guarda de datas reprova esse padrão fora da aritmética de calendário.
  const conferido = new Date(Date.UTC(ano, mes - 1, dia));
  return conferido.getUTCFullYear() === ano &&
    conferido.getUTCMonth() === mes - 1 &&
    conferido.getUTCDate() === dia
    ? valor
    : undefined;
}

/** O par `periodoDe`/`periodoAte` da URL, dias `AAAA-MM-DD` inclusivos. */
export interface PeriodoDaBusca {
  periodoDe?: string;
  periodoAte?: string;
}

/**
 * O período da busca, saneado num lugar só (a rota e a tela chamam esta
 * função): dia que não existe cai, e período invertido cai INTEIRO — a API o
 * recusa com 400, e uma URL salva ou digitada ao contrário abriria a tela de
 * erro em vez da lista.
 */
export function periodoDaBusca(busca: {
  periodoDe?: unknown;
  periodoAte?: unknown;
}): PeriodoDaBusca {
  const de = diaDeCalendario(busca.periodoDe);
  const ate = diaDeCalendario(busca.periodoAte);
  if (de && ate && de > ate) return {};
  return { ...(de ? { periodoDe: de } : {}), ...(ate ? { periodoAte: ate } : {}) };
}

/** `AAAA-MM-DD` → `dd/mm/aaaa`, por fatiamento de texto (sem fuso). */
export function diaPorExtenso(dia: string): string {
  const [ano, mes, diaDoMes] = dia.split("-");
  return `${diaDoMes}/${mes}/${ano}`;
}

/** "de 12/10/2026 a 18/10/2026", "a partir de 12/10/2026" ou "até 18/10/2026". */
export function rotuloDoPeriodo(periodoDe?: string, periodoAte?: string): string {
  if (periodoDe && periodoAte) {
    return periodoDe === periodoAte
      ? `em ${diaPorExtenso(periodoDe)}`
      : `de ${diaPorExtenso(periodoDe)} a ${diaPorExtenso(periodoAte)}`;
  }
  if (periodoDe) return `a partir de ${diaPorExtenso(periodoDe)}`;
  if (periodoAte) return `até ${diaPorExtenso(periodoAte)}`;
  return "";
}

/**
 * O último dia do balde da Série temporal: a semana começa na segunda (o
 * balde é a chave do dia de início), e o mês, no dia 1. O fim é INCLUSIVO,
 * como `periodoAte`. Espelho do que a API usa para contar o balde.
 */
export function ultimoDiaDoBalde(inicio: string, periodo: "semana" | "mes"): string {
  if (periodo === "semana") return somarDias(inicio, 6);
  const [ano = 0, mes = 1] = inicio.split("-").map(Number);
  // O dia 0 do mês seguinte é o último deste; o número do dia basta, o mês e o
  // ano já são os do balde.
  const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  return `${String(ano).padStart(4, "0")}-${String(mes).padStart(2, "0")}-${String(ultimo).padStart(2, "0")}`;
}

/**
 * TUT-578 — o começo do balde que contém `dia`: a segunda-feira da semana, ou o
 * dia 1 do mês (o par de `ultimoDiaDoBalde`). A Série "Entregas a vencer" parte
 * do balde de hoje, o dia do escritório.
 */
export function inicioDoBalde(dia: string, periodo: "semana" | "mes"): string {
  if (periodo === "mes") return `${dia.slice(0, 7)}-01`;
  const [ano = 0, mes = 1, d = 1] = dia.split("-").map(Number);
  const diaDaSemana = new Date(Date.UTC(ano, mes - 1, d)).getUTCDay();
  return somarDias(dia, -((diaDaSemana + 6) % 7));
}

export function periodoDoAtalho(atalho: AtalhoDePeriodo, hoje: string): PeriodoDeDatas {
  switch (atalho) {
    case "30-dias":
      return { de: somarDias(hoje, -29), ate: hoje };
    case "90-dias":
      return { de: somarDias(hoje, -89), ate: hoje };
    case "ano":
      return { de: `${hoje.slice(0, 4)}-01-01`, ate: hoje };
  }
}

/** O atalho que reproduz o período inteiro, ou `null`. */
export function atalhoAtivo(periodo: PeriodoDeDatas, hoje: string): AtalhoDePeriodo | null {
  return (
    ATALHOS_DE_PERIODO.find(({ chave }) => {
      const esperado = periodoDoAtalho(chave, hoje);
      return esperado.de === periodo.de && esperado.ate === periodo.ate;
    })?.chave ?? null
  );
}

/** "De" depois de "até" não é lista vazia: é pedido errado, e não vai ao servidor. */
export function periodoInvertido(periodo: PeriodoDeDatas): boolean {
  return periodo.de !== "" && periodo.ate !== "" && periodo.de > periodo.ate;
}
